import {
  analyzeMonth, COST_GROUPS, findingText, summaryText, type BridgeStep, type CostGroup, type Me, type MonthAnalysis, type MonthFigures, type ProjectAnalysisData,
} from '@ft/shared';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Loading, useLoad } from '../components/ui';
import { getLang, useI18n, type T } from '../i18n';
import { GROUP_COLOR, SEV_TONE } from '../lib/analysis';
import { dtfmt, eur } from '../lib/format';
import { api } from '../lib/http';
import { Chip, Panel } from '../lib/collapse';

/**
 * Projekt-Deep-Dive: Umsatz, Kosten nach Kostengruppe und Marge je Monat, „Was ist passiert?“ für den gewählten Monat
 * gegenüber dem Vormonat (gemeinsame Analyse aus shared/analysis.ts, dieselben Texte wie im Report), Kommentar des PMs
 * und Kostenaufschlüsselung mit Einzelposten.
 */
type Status = 'ok' | 'watch' | 'crit';
const loc = () => (getLang() === 'en' ? 'en-GB' : 'de-DE');
const pctTxt = (v: number | null) => (v === null ? '–' : `${v < 0 ? '−' : ''}${Math.abs(Math.round(v * 100))}${getLang() === 'en' ? '%' : ' %'}`);
const signedPct = (v: number) => { const r = Math.round(v * 100); return `${r > 0 ? '+' : r < 0 ? '−' : '±'}${Math.abs(r)}${getLang() === 'en' ? '%' : ' %'}`; };
const k1 = (c: number) => (c ? (c / 100_000).toLocaleString(loc(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).replace('-', '−') : '–');
const kEur = (c: number, signed = false) => `${signed ? (c > 0 ? '+' : c < 0 ? '−' : '±') : c < 0 ? '−' : ''}${(Math.abs(c) / 100_000).toLocaleString(loc(), { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${getLang() === 'en' ? 'k€' : 'T€'}`;
const hrs = (h: number) => `${Math.round(h).toLocaleString(loc())} h`;
const monthName = (i: number, style: 'short' | 'long' = 'short') => new Date(Date.UTC(2026, i, 1)).toLocaleString(loc(), { month: style, timeZone: 'UTC' });
const statusOf = (p: number | null, target: number): Status => (p === null || p < target - 0.1 ? 'crit' : p < target ? 'watch' : 'ok');
const TONE: Record<Status, string> = { ok: 'pos', watch: 'crit', crit: 'neg' };
const COLOR: Record<Status, string> = { ok: 'var(--pos)', watch: 'var(--crit)', crit: 'var(--neg)' };
const costOf = (m: MonthFigures) => m.costs.personnel + m.costs.subcontracting + m.costs.other;
const rel = (a: number, b: number) => (b ? (a - b) / b : 0);

export function ProjectAnalysisPage({ me }: { me: Me }) {
  const { t, lang } = useI18n();
  const { wd = '' } = useParams();
  const [sp, setSp] = useSearchParams();
  const year = sp.get('jahr');
  const { data, error, reload } = useLoad<ProjectAnalysisData>(`/api/margin/project/${encodeURIComponent(wd)}${year ? `?year=${year}` : ''}`);
  void me;

  const view = useMemo(() => {
    if (!data) return null;
    const figures: MonthFigures[] = data.months.map((m) => ({ netSalesCents: m.netSalesCents, hours: m.hours, costs: m.costs, costDetail: m.costDetail }));
    const closed = new Set(data.closedMonths.map((m) => m - 1));
    const lastClosed = data.closedMonths.length ? Math.max(...data.closedMonths) - 1 : -1;
    const lastData = figures.reduce((a, m, i) => (m.netSalesCents || costOf(m) ? i : a), -1);
    const shown = Math.min(12, Math.max(lastClosed, lastData) + 2); // ein offener Monat dahinter
    let first = 0;
    while (first < 11 && !figures[first]!.netSalesCents && !costOf(figures[first]!)) first++;
    const yearIdx = [...closed].filter((i) => i <= lastClosed);
    const sum = (f: (m: MonthFigures) => number) => yearIdx.reduce((s, i) => s + f(figures[i]!), 0);
    const ns = sum((m) => m.netSalesCents), cost = sum(costOf), hours = sum((m) => m.hours);
    const groups = Object.fromEntries(COST_GROUPS.map((g) => [g, sum((m) => m.costs[g])])) as Record<CostGroup, number>;
    return { figures, closed, lastClosed, shown, first, yearIdx, ns, cost, hours, groups, marginPct: ns ? (ns - cost) / ns : null };
  }, [data]);

  const defaultMonth = view ? Math.max(view.lastClosed, 0) : 0;
  const [sel, setSel] = useState<number | null>(null);
  useEffect(() => setSel(null), [wd, year]);
  const month = sel ?? defaultMonth;
  const target = (data?.target ?? 45) / 100;
  const a = useMemo(() => (view ? analyzeMonth(view.figures, month, target) : null), [view, month, target]);

  if (!data || !view || !a) return <section className="page"><Crumbs t={t} year={year} /><Loading error={error} /></section>;
  const ystatus = statusOf(view.marginPct, target);
  const lc = view.lastClosed;
  const lcPct = lc >= 0 && view.figures[lc]!.netSalesCents ? (view.figures[lc]!.netSalesCents - costOf(view.figures[lc]!)) / view.figures[lc]!.netSalesCents : null;
  const prevPct = lc > 0 && view.figures[lc - 1]!.netSalesCents ? (view.figures[lc - 1]!.netSalesCents - costOf(view.figures[lc - 1]!)) / view.figures[lc - 1]!.netSalesCents : null;
  const range = view.yearIdx.length ? `${monthName(Math.min(...view.yearIdx))} – ${monthName(Math.max(...view.yearIdx))} ${data.year}` : String(data.year);
  const setYear = (y: string) => { const n = new URLSearchParams(sp); n.set('jahr', y); setSp(n, { replace: true }); };

  return (
    <section className="page">
      <Crumbs t={t} year={year} />
      <div className="box pahead">
        <div className="pahead-main">
          <div className="row" style={{ alignItems: 'baseline' }}>
            <h1>{data.name}</h1>
            <span className="mono muted">{data.workdayProjectId}</span>
            <span className={`pill ${TONE[ystatus]}`}>{t(`mg.state.${ystatus}`)}</span>
            {!data.project && <span className="muted small">{t('mg.noTool')}</span>}
          </div>
          <div className="pameta">
            <span>{t('pa.customer')} <b>{data.customer || '–'}</b></span>
            <span>{t('pa.pm')} <b>{data.pm ?? '–'}</b>{data.deputy && <> · {t('pa.deputy')} <b>{data.deputy}</b></>}</span>
            <span>{t('mg.target')} <b>{pctTxt(target)}</b> ({t(`pa.target.${data.targetSource}`)})</span>
            <span>{t('pa.source', { range })}</span>
          </div>
        </div>
        <div className="row">
          {data.years.length > 1 && (
            <div className="fld"><label htmlFor="pa-y">{t('mg.year')}</label>
              <select id="pa-y" value={data.year} onChange={(e) => setYear(e.target.value)}>{data.years.map((y) => <option key={y}>{y}</option>)}</select>
            </div>
          )}
          {data.project && <Link className="btn" to={`/report?themen=analysis&projekte=${data.project.id}`}>{t('pa.toReport')}</Link>}
        </div>
      </div>

      <Panel id="pa.kpis" title={t('mg.kpisTitle', { y: data.year })}
        summary={<><Chip>{t('mg.legendNs')} <b>{eur(view.ns)}</b></Chip><Chip tone={ystatus === 'ok' ? 'pos' : ystatus === 'watch' ? 'crit' : 'neg'}>{t('mg.col.margin')} <b>{pctTxt(view.marginPct)}</b></Chip></>}>
      <div className="kpis">
        <div className="kpi"><div className="lbl">{t('pa.kpi.ns', { y: data.year })}</div><div className="val num">{eur(view.ns)}</div><div className="sub">{range} · {t('pa.kpi.hours', { h: Math.round(view.hours).toLocaleString(loc()) })}</div></div>
        <div className="kpi"><div className="lbl">{t('pa.kpi.cost', { y: data.year })}</div><div className="val num">{eur(view.cost)}</div>
          <div className="sub">{COST_GROUPS.filter((g) => view.groups[g]).map((g) => `${t(`pa.group.${g}`)} ${pctTxt(view.cost ? view.groups[g] / view.cost : 0)}`).join(' · ') || '–'}</div></div>
        <div className="kpi"><div className="lbl">{t('pa.kpi.margin', { y: data.year })}</div><div className="val num" style={{ color: COLOR[ystatus] }}>{pctTxt(view.marginPct)}</div><div className="sub">{eur(view.ns - view.cost)} · {t('pa.kpi.target', { p: pctTxt(target) })}</div></div>
        <div className="kpi"><div className="lbl">{t('pa.kpi.last', { m: lc >= 0 ? monthName(lc, 'long') : '–' })}</div><div className="val num" style={{ color: lcPct === null ? 'var(--muted)' : COLOR[statusOf(lcPct, target)] }}>{pctTxt(lcPct)}</div>
          <div className="sub">{lcPct === null || prevPct === null ? t('pa.kpi.noPrev') : t('pa.kpi.pts', { d: `${Math.round((lcPct - prevPct) * 100) > 0 ? '+' : Math.round((lcPct - prevPct) * 100) < 0 ? '−' : '±'}${Math.abs(Math.round((lcPct - prevPct) * 100))}`, m: monthName(lc - 1, 'long') })}</div></div>
      </div>
      </Panel>

      <MonthBars figures={view.figures} closed={view.closed} shown={view.shown} first={view.first} sel={month} target={target} onPick={setSel} t={t} />

      <AnalysisCard a={a} data={data} month={month} t={t} lang={lang} onSaved={reload} />

      <CostTable data={data} figures={view.figures} shown={view.shown} sel={month} closed={view.closed} t={t} />
    </section>
  );
}

function Crumbs({ t, year }: { t: T; year: string | null }) {
  return <div className="crumbs"><Link to={`/marge${year ? `?jahr=${year}` : ''}`}>{t('mg.title')}</Link><span>›</span><span>{t('pa.title')}</span></div>;
}

function MonthBars({ figures, closed, shown, first, sel, target, onPick, t }: { figures: MonthFigures[]; closed: Set<number>; shown: number; first: number; sel: number; target: number; onPick: (i: number) => void; t: T }) {
  const list = figures.slice(0, shown);
  const max = Math.max(1, ...list.map((m) => Math.max(m.netSalesCents, costOf(m))));
  const used = COST_GROUPS.filter((g) => list.some((m) => m.costs[g]));
  return (
    <Panel id="pa.chart" title={t('pa.chart')} hint={t('pa.chartHint')}
      summary={list[sel] ? <Chip tone="info">{monthName(sel)} · {t('mg.legendNs')} <b>{eur(list[sel]!.netSalesCents)}</b></Chip> : undefined}
      actions={<>
        <span className="mglegend"><i className="pa-ns" />{t('mg.legendNs')}</span>
        {used.map((g) => <span key={g} className="mglegend"><i style={{ background: GROUP_COLOR[g] }} />{t(`pa.group.${g}`)}</span>)}
        <span className="hint">{t('pa.chartPct')}</span>
      </>}>
      <div className="pachart" style={{ gridTemplateColumns: `repeat(${list.length}, minmax(0, 1fr))` }}>
        {list.map((m, i) => {
          const open = !closed.has(i);
          const pct = m.netSalesCents > 0 ? (m.netSalesCents - costOf(m)) / m.netSalesCents : null;
          const can = !open && i >= first;
          const label = open ? t('pa.open') : pctTxt(pct);
          const content = (
            <>
              <span className="pa-pct" style={{ color: open || pct === null ? 'var(--faint)' : COLOR[statusOf(pct, target)] }}>{label}</span>
              <span className="pa-bars">
                <i className="pa-ns" style={{ height: `${(m.netSalesCents / max) * 100}%` }} />
                <span className="pa-stack" style={{ height: `${(costOf(m) / max) * 100}%` }}>
                  {[...COST_GROUPS].reverse().map((g) => m.costs[g] > 0 && <i key={g} title={`${t(`pa.group.${g}`)} ${eur(m.costs[g])}`} style={{ flexGrow: m.costs[g], background: GROUP_COLOR[g] }} />)}
                </span>
              </span>
              <span className="pa-mon">{monthName(i)}</span>
            </>
          );
          return can
            ? <button key={i} type="button" className={`pacol${i === sel ? ' sel' : ''}`} aria-pressed={i === sel} aria-label={t('pa.pick', { m: monthName(i, 'long') })} onClick={() => onPick(i)}>{content}</button>
            : <div key={i} className={`pacol${open ? ' open' : ''}`} aria-label={open ? t('pa.openMonth', { m: monthName(i, 'long') }) : undefined}>{content}</div>;
        })}
      </div>
    </Panel>
  );
}

const BRIDGE_KEY: Record<Exclude<BridgeStep['key'], 'marginPrev' | 'marginCur'>, string> = { netSales: 'mg.legendNs', personnel: 'pa.group.personnel', subcontracting: 'pa.group.subcontracting', other: 'pa.group.other', costs: 'pa.costs' };

function AnalysisCard({ a, data, month, t, lang, onSaved }: { a: MonthAnalysis; data: ProjectAnalysisData; month: number; t: T; lang: 'de' | 'en'; onSaved: () => void }) {
  const cur = monthName(month, 'long'), prev = monthName(a.prevMonth, 'long');
  const steps = a.bridge.map((b) => ({
    label: b.key === 'marginPrev' ? t('pa.marginOf', { m: monthName(a.prevMonth) }) : b.key === 'marginCur' ? t('pa.marginOf', { m: monthName(month) }) : t(BRIDGE_KEY[b.key] as 'pa.costs'),
    v: b.valueCents, total: b.key === 'marginPrev' || b.key === 'marginCur',
  }));
  let run = 0, lo = 0, hi = 0;
  const segs = steps.map((s) => { const a0 = s.total ? 0 : run, a1 = s.total ? s.v : run + s.v; run = a1; lo = Math.min(lo, a0, a1); hi = Math.max(hi, a0, a1); return [a0, a1] as const; });
  const span = hi - lo || 1;
  const X = (v: number) => ((v - lo) / span) * 100;
  const c = a.cur, p = a.prev;
  const tone = (x: number, good: boolean) => (Math.abs(x) < 0.03 ? 'var(--muted)' : (x > 0) === good ? 'var(--pos)' : 'var(--neg)');
  const perf: [string, string, string, string, string][] = [
    [t('mg.legendNs'), eur(p.netSalesCents), eur(c.netSalesCents), signedPct(rel(c.netSalesCents, p.netSalesCents)), tone(rel(c.netSalesCents, p.netSalesCents), true)],
    [t('mg.legendCost'), eur(p.costCents), eur(c.costCents), signedPct(rel(c.costCents, p.costCents)), tone(rel(c.costCents, p.costCents), false)],
    [t('mg.col.margin'), pctTxt(p.marginPct), pctTxt(c.marginPct), p.marginPct === null || c.marginPct === null ? '–' : t('pa.ptsShort', { d: `${Math.round((c.marginPct - p.marginPct) * 100) > 0 ? '+' : Math.round((c.marginPct - p.marginPct) * 100) < 0 ? '−' : '±'}${Math.abs(Math.round((c.marginPct - p.marginPct) * 100))}` }), tone((c.marginPct ?? 0) - (p.marginPct ?? 0), true)],
    [t('mg.col.hours'), hrs(p.hours), hrs(c.hours), signedPct(rel(c.hours, p.hours)), 'var(--muted)'],
    [t('pa.perHourNs'), p.salesPerHourCents === null ? '–' : eur(p.salesPerHourCents), c.salesPerHourCents === null ? '–' : eur(c.salesPerHourCents), p.salesPerHourCents && c.salesPerHourCents ? signedPct(rel(c.salesPerHourCents, p.salesPerHourCents)) : '–', p.salesPerHourCents && c.salesPerHourCents ? tone(rel(c.salesPerHourCents, p.salesPerHourCents), true) : 'var(--muted)'],
    [t('pa.perHourCost'), p.costPerHourCents === null ? '–' : eur(p.costPerHourCents), c.costPerHourCents === null ? '–' : eur(c.costPerHourCents), p.costPerHourCents && c.costPerHourCents ? signedPct(rel(c.costPerHourCents, p.costPerHourCents)) : '–', p.costPerHourCents && c.costPerHourCents ? tone(rel(c.costPerHourCents, p.costPerHourCents), false) : 'var(--muted)'],
  ];
  return (
    <Panel id="pa.what" className="pacard" title={t('pa.what', { cur, prev })}
      hint={<><span className={`pill ${SEV_TONE[a.severity]}`}>{t(`pa.sev.${a.severity}`)}</span> {t('pa.auto')}</>}
      summary={<span className={`pill ${SEV_TONE[a.severity]}`}>{t(`pa.sev.${a.severity}`)}</span>}>
      <p className="pasummary">{summaryText(a, lang)}</p>
      {!a.costDetail && <p className="notice warn pahint">{t('pa.noDetail', { m: `${prev} / ${cur}` })}</p>}
      <div className="pasplit">
        <div className="pabridge">
          <div className="lbl">{t('pa.bridge', { from: monthName(a.prevMonth), to: monthName(month) })}</div>
          {steps.map((s, i) => {
            const [a0, a1] = segs[i]!;
            const cls = s.total ? (s.v < 0 ? 'neg' : 'tot') : s.v >= 0 ? 'up' : 'down';
            return (
              <div key={i} className={`pabr${s.total ? ' total' : ''}`}>
                <span>{s.label}</span>
                <span className="track"><span className="zero" style={{ left: `${X(0)}%` }} /><i className={cls} style={{ left: `${Math.min(X(a0), X(a1))}%`, width: `${Math.max(Math.abs(X(a1) - X(a0)), 0.6)}%` }} /></span>
                <span className={`num v ${s.total ? '' : Math.abs(s.v) < 5_000 ? 'muted' : s.v >= 0 ? 'pos' : 'neg'}`}>{s.total ? kEur(s.v) : kEur(s.v, true)}</span>
              </div>
            );
          })}
          <span className="muted small">{t('pa.bridgeHint')}</span>
        </div>
        <div className="paperf">
          <div className="lbl">{t('pa.perf')}</div>
          <table>
            <thead><tr><th /><th className="r">{monthName(a.prevMonth)}</th><th className="r cur">{monthName(month)}</th><th className="r">Δ</th></tr></thead>
            <tbody>{perf.map(([l, x, y, d, col]) => <tr key={l}><td>{l}</td><td className="r num nowrap">{x}</td><td className="r num nowrap" style={{ fontWeight: 600 }}>{y}</td><td className="r num nowrap" style={{ color: col }}>{d}</td></tr>)}</tbody>
          </table>
        </div>
      </div>
      <ul className="pafinds">
        {a.findings.map((f, i) => {
          const tx = findingText(f, month, lang);
          return (
            <li key={i}>
              <span className={`pill ${SEV_TONE[f.severity]}`}>{t(`pa.sev.${f.severity}`)}</span>
              <span><b>{tx.title}</b><span className="pa-ftext">{tx.text}</span>{tx.hint && <span className="muted small">{tx.hint}</span>}</span>
            </li>
          );
        })}
      </ul>
      <Comment data={data} month={month} t={t} onSaved={onSaved} />
    </Panel>
  );
}

function Comment({ data, month, t, onSaved }: { data: ProjectAnalysisData; month: number; t: T; onSaved: () => void }) {
  const key = `${data.year}-${String(month + 1).padStart(2, '0')}`;
  const saved = data.comments.find((c) => c.month === key);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setDraft(saved?.text ?? ''); setErr(null); }, [key, saved?.text]);
  async function save(text: string | null) {
    setBusy(true); setErr(null);
    try { await api('PUT', '/api/margin/comments', { workdayProjectId: data.workdayProjectId, month: key, text }); onSaved(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  if (!saved && !data.canComment) return null;
  return (
    <div className="pacomment">
      <label htmlFor="pa-comment"><b>{t('pa.comment')}</b> <span className="muted small">{t('pa.commentHint')}</span></label>
      {saved && (
        <div className="pacomment-saved">
          <span style={{ whiteSpace: 'pre-line' }}>{saved.text}</span>
          <span className="muted small">{t('pa.commentMeta', { by: saved.by ?? '–', at: dtfmt(saved.at), m: monthName(month, 'long') })}</span>
        </div>
      )}
      {data.canComment && (
        <>
          <textarea id="pa-comment" rows={2} maxLength={2000} value={draft} placeholder={t('pa.commentPh')} onChange={(e) => setDraft(e.target.value)} disabled={busy} />
          <div className="row">
            <button className="btn primary" disabled={busy || !draft.trim() || draft.trim() === saved?.text} onClick={() => void save(draft)}>{t('pa.commentSave')}</button>
            {saved && <button className="btn ghost danger" disabled={busy} onClick={() => void save(null)}>{t('pa.commentDelete')}</button>}
            {err && <span className="err small" role="alert">{err}</span>}
          </div>
        </>
      )}
    </div>
  );
}

function CostTable({ data, figures, shown, sel, closed, t }: { data: ProjectAnalysisData; figures: MonthFigures[]; shown: number; sel: number; closed: Set<number>; t: T }) {
  const [open, setOpen] = useState<Set<CostGroup>>(new Set());
  const months = Array.from({ length: Math.min(shown, 12) }, (_, i) => i).filter((i) => closed.has(i) || figures[i]!.netSalesCents || costOf(figures[i]!));
  const total = (f: (i: number) => number) => months.reduce((s, i) => s + f(i), 0);
  const allCost = total((i) => costOf(figures[i]!));
  const hl = (i: number) => (i === sel ? 'sel' : '');
  const noDetail = months.filter((i) => !data.months[i]!.costDetail);
  return (
    <Panel id="pa.table" title={t('pa.table')} hint={t('pa.tableHint')} summary={<Chip>{t('pa.groupsN', { n: COST_GROUPS.filter((g) => months.some((i) => figures[i]!.costs[g])).length })} · <b>{eur(allCost)}</b></Chip>}>
      <div className="tablewrap">
        <table className="patable">
          <thead><tr><th>{t('pa.col.kind')}</th>{months.map((i) => <th key={i} className={`r ${hl(i)}`}>{monthName(i)}{!data.months[i]!.costDetail && '*'}</th>)}<th className="r">{t('mg.sum')}</th><th className="r">{t('pa.col.share')}</th></tr></thead>
          <tbody>
            <tr className="pa-nsrow"><td>{t('mg.legendNs')}</td>{months.map((i) => <td key={i} className={`r num ${hl(i)}`}>{k1(figures[i]!.netSalesCents)}</td>)}<td className="r num">{k1(total((i) => figures[i]!.netSalesCents))}</td><td /></tr>
            {COST_GROUPS.map((g) => {
              const sum = total((i) => figures[i]!.costs[g]);
              if (!sum) return null;
              const items = data.items.filter((x) => x.group === g);
              const isOpen = open.has(g);
              return (
                <Fragment key={g}>
                  <tr className="pa-group">
                    <td>
                      {items.length
                        ? <button type="button" className="pa-toggle" aria-expanded={isOpen} onClick={() => setOpen((o) => { const n = new Set(o); if (n.has(g)) n.delete(g); else n.add(g); return n; })}><span className="caret">{isOpen ? '▾' : '▸'}</span><i style={{ background: GROUP_COLOR[g] }} />{t(`pa.group.${g}`)}</button>
                        : <span className="pa-toggle"><span className="caret" /><i style={{ background: GROUP_COLOR[g] }} />{t(`pa.group.${g}`)}</span>}
                    </td>
                    {months.map((i) => <td key={i} className={`r num ${hl(i)}`}>{k1(figures[i]!.costs[g])}</td>)}
                    <td className="r num">{k1(sum)}</td><td className="r num muted">{pctTxt(allCost ? sum / allCost : 0)}</td>
                  </tr>
                  {isOpen && items.map((it, k) => {
                    const s = months.reduce((x, i) => x + (it.months[i] ?? 0), 0);
                    return (
                      <tr key={k} className="pa-item">
                        <td title={[it.heading2, it.cat2, it.cat3].filter(Boolean).join(' › ')}>{it.object}</td>
                        {months.map((i) => <td key={i} className={`r num ${hl(i)}`}>{k1(it.months[i] ?? 0)}</td>)}
                        <td className="r num">{k1(s)}</td><td className="r num muted">{pctTxt(allCost ? s / allCost : 0)}</td>
                      </tr>
                    );
                  })}
                </Fragment>
              );
            })}
            <tr className="pa-total"><td>{t('pa.costs')}</td>{months.map((i) => <td key={i} className={`r num ${hl(i)}`}>{k1(costOf(figures[i]!))}</td>)}<td className="r num">{k1(allCost)}</td><td className="r num muted">{allCost ? pctTxt(1) : ''}</td></tr>
            <tr className="pa-margin"><td>{t('mg.col.margin')}</td>{months.map((i) => {
              const m = figures[i]!;
              const p = m.netSalesCents > 0 ? (m.netSalesCents - costOf(m)) / m.netSalesCents : null;
              return <td key={i} className={`r num ${hl(i)}`} style={{ color: p === null ? 'var(--faint)' : COLOR[statusOf(p, data.target / 100)] }}>{pctTxt(p)}</td>;
            })}{(() => { const n = total((i) => figures[i]!.netSalesCents); const p = n ? (n - allCost) / n : null; return <td className="r num" style={{ color: p === null ? undefined : COLOR[statusOf(p, data.target / 100)] }}>{pctTxt(p)}</td>; })()}<td /></tr>
          </tbody>
        </table>
      </div>
      {(noDetail.length > 0 || !data.hasCostData) && <p className="muted small" style={{ margin: 0, padding: '10px 18px 14px' }}>{data.hasCostData ? t('pa.noDetailMonths', { m: noDetail.map((i) => monthName(i)).join(', ') }) : t('pa.noCostData')}</p>}
    </Panel>
  );
}
